import {
  SET_CARD_ERROR_OCCURED,
  LOADING_OTP,
  OTP_CONFIRMATION,
  OTP_CREATION,
  OTP_ERROR,
  OTP_CLEAR,
  REDEEM_MODAL_TOGGLE,
  SET_NIT_CODE_GENERATED,
  SET_NIT_CODE_VALIDATED,
  TRIGGER_3DS_RETRY,
} from 'constants/ActionTypes';
import { purchase } from 'js-api-client';
import { createPayment, isPaymentPreconditionError } from '@/services/purchase/createPayment';
import { camelizeKeys, decamelizeKeys } from 'humps';
import i18n from 'i18next';
import { selectCardCheckoutDecision } from 'selectors/cardCheckoutSelectors';
import { getCybersourceDisplayCode, getCybersourceErrorKey } from 'utils/Cybersource';
import { getSessionId } from 'utils/session';
import { getSearchParams, generateSearchRedirectUrl } from 'utils/searchUrlHelpers';
import { getRnMixpanelBridgedTrackerId } from 'utils/rnMixpanelNativeProps';
import store from '@/store';
import sanitizeCardNumber from 'utils/sanitizeCardNumber';
import { trackPurchaseStepError } from 'metrics/user-analytics/errorTracker';
import { setError } from '.';
import {
  getPurchase,
  updatePurchase,
  receivePurchase,
  receivePayment,
  requestPayment,
  expirePurchase,
  refreshStoreReferenceIssued,
  recoverFromBfoPriceRefresh,
} from './purchase';
import {
  isBfoRefreshRequiredPaymentError,
  isBfoReturnedPurchase,
  isBfoSupportedJourney,
} from '../utils/purchase/baseFareOverride';
import availablePaymentEngines from '../constants/PaymentEngine';
import storeFactory from '../../payments/core/factories/store/storeFactory';
import transferFactory from '../../payments/core/factories/transfer';
import cardFactory from '../../payments/core/factories/card';
import bnplFactory from '../../payments/core/factories/bnpl';
import walletFactory from '../../payments/core/factories/wallet';
import { ssoError } from './sso';
import errorMonitoring from '../errorMonitoring';
/**
 * Constructs the payment payload with necessary billing and payment information.
 *
 * @param {string} type - The type of payment, defaults to 'credit_card'.
 * @param {string} engine - The payment engine to be used.
 * @param {Object} cardValues - The card values and billing address information.
 * @returns {Object} The payment payload object.
 */
export function getPaymentPayload(type = 'credit_card', engine, cardValues = {}) {
  const { addressAttributes } = cardValues;
  const hasAddressAttributes = addressAttributes && Object.keys(addressAttributes).length > 0;

  return {
    payment_type: type,
    payment_engine: type === 'free_pay' ? undefined : engine,
    tracker_id: getRnMixpanelBridgedTrackerId(),
    device_session_id: getSessionId(),
    monthly_selected_plan: cardValues.monthlySelectedPlan,
    // Funnel only sends the user's storage intent. After a successful payment,
    // Purchase coordinates the stored-card persistence with Reserhub.
    save_card: Boolean(cardValues.saveCard),
    ...(hasAddressAttributes && {
      address_attributes: decamelizeKeys(addressAttributes),
    }),
  };
}

/** Return whether the current card payment should use the Basis Theory strategy. */
function shouldUseBasisTheoryStrategy(state, paymentMethod, cardValues, submittedCheckoutMode) {
  const hasBasisTheoryReference = Boolean(
    cardValues?.basisTheoryTokenIntentId ||
      cardValues?.basisTheoryTokenId ||
      cardValues?.vaultCardId,
  );

  if (submittedCheckoutMode === 'basis_theory') {
    return hasBasisTheoryReference;
  }

  const cardCheckoutDecision = selectCardCheckoutDecision(state, paymentMethod);

  return cardCheckoutDecision.mode === 'basis_theory' && hasBasisTheoryReference;
}

/**
 * Action creator for setting the card error occurred state.
 *
 * @returns {Object} An action object containing the type SET_CARD_ERROR_OCCURED.
 */
function setCardErrorOccured() {
  return {
    type: SET_CARD_ERROR_OCCURED,
  };
}

/**
 * Action creator for setting the loading state and visibility of the OTP modal.
 *
 * @param {Object} payload - The payload containing the loading state and OTP modal visibility.
 * @param {boolean} payload.loading - The loading state to be set.
 * @param {boolean} payload.showOTPModal - The visibility state of the OTP modal.
 * @returns {Object} An action object containing the type LOADING_OTP and the payload.
 */
export function loadingOTP({ loading, showOTPModal }) {
  return { type: LOADING_OTP, payload: { loading, showOTPModal } };
}

/**
 * Action creator for initiating the OTP creation process.
 *
 * @param {Object} payload - The payload containing the OTP modal visibility state.
 * @param {boolean} payload.showOTPModal - The visibility state of the OTP modal.
 * @returns {Object} An action object containing the type OTP_CREATION and the payload.
 */
export function otpCreation({ showOTPModal }) {
  return { type: OTP_CREATION, payload: { loading: false, showOTPModal } };
}

/**
 * Action creator for confirming the OTP.
 *
 * @param {Object} payload - The payload containing the OTP confirmation state and OTP modal visibility.
 * @param {boolean} payload.otpConfirmed - The state indicating if the OTP was confirmed.
 * @param {boolean} payload.showOTPModal - The visibility state of the OTP modal.
 * @returns {Object} An action object containing the type OTP_CONFIRMATION and the payload.
 */
export function otpConfirmation({ otpConfirmed, showOTPModal }) {
  return { type: OTP_CONFIRMATION, payload: { otpConfirmed, showOTPModal } };
}

/**
 * Action creator for setting the OTP error state.
 *
 * @param {Object} payload - The payload containing the OTP error message and error code.
 * @param {string} payload.otpError - The error message for the OTP error.
 * @param {number} payload.otpErrorCode - The error code for the OTP error.
 * @returns {Object} An action object containing the type OTP_ERROR and the payload.
 */
export function otpError({ otpError, otpErrorCode }) {
  return { type: OTP_ERROR, payload: { otpError, otpErrorCode } };
}

/**
 * Retrieves the endpoint for OTP based on the wallet type from the state.
 *
 * @returns {string} The endpoint for the OTP service.
 */
function getOTPEndpoint() {
  const { walletType } = store.getState().purchase.toJS();
  return walletType;
}

/**
 * Clean the OTP state
 */
export function clearOTP() {
  return { type: OTP_CLEAR };
}

/**
 * Initiates the OTP generation process.
 *
 * @param {number} pointsUsed - The number of points to be used for the OTP generation.
 * @returns {Function} A thunk action that dispatches actions based on the OTP generation process.
 */
export function generateOTP(pointsUsed) {
  return (dispatch) => {
    const {
      whitelabelConfig: { features },
    } = store.getState();
    dispatch(loadingOTP({ loading: true, showOTPModal: false }));
    const payload = { max_redemption_points: pointsUsed };
    purchase
      .generateOTP(getOTPEndpoint(), payload)
      .then(() => {
        dispatch(otpCreation({ showOTPModal: true }));
      })
      .catch((error) => {
        const { code } = error;
        dispatch(loadingOTP({ loading: false, showOTPModal: false }));
        if (code === 401 && features.USE_SSO) {
          dispatch(ssoError(error.code));
        } else {
          dispatch(setError(code, 'otp_creation_error', 'error', false));
        }
      });
  };
}

/** REDEEM FLOW */

/**
 * Toggles the visibility state of the redeem modal.
 * @param {boolean} show - The visibility state of the redeem modal.
 * @returns {Object} An action object containing the type REDEEM_MODAL_TOGGLE.
 */
export const redeemModalToggle = (show) => ({ type: REDEEM_MODAL_TOGGLE, payload: show });

/**
 * Generates the NIT code for the user.
 *
 * @param {string} notificationType - sms or email. The type of notification to be generated.
 * @returns {Function} A thunk action that dispatches actions based on the NIT code generation process.
 */
export function generateNITCode({ notificationType }) {
  return async (dispatch, getState) => {
    const {
      whitelabelConfig: {
        env: {
          api: { searchUrl },
        },
      },
      costapass,
    } = getState();
    const {
      profile: { token: costaPassToken },
    } = costapass.toJS();

    try {
      const response = await fetch(`${searchUrl}/v2/ewallet/nits`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${costaPassToken}`,
        },
        body: JSON.stringify({ notification_type: notificationType }),
      });

      const data = await response.json();

      if (response.status === 401) {
        const errorMessage = data.message || 'Unauthorized access to generate NIT code';
        throw new Error(errorMessage);
      }

      if (response.status === 422) {
        const errorMessage = data.message || 'Failed to generate NIT code';
        throw new Error(errorMessage);
      }

      if (!response.ok) {
        throw new Error('Unexpected error from server');
      }

      dispatch({ type: SET_NIT_CODE_GENERATED, payload: data });

      return data;
    } catch (error) {
      // If it's already our custom error, re-throw it
      if (error.message === 'Failed to fetch' || error.message.includes('NetworkError')) {
        throw error;
      }
      // For network errors, throw generic message
      throw error;
    }
  };
}

/**
 * Validates the NIT code for the user.
 *
 * @param {string} nitCode - The NIT code to be validated.
 * @returns {Function} A thunk action that dispatches actions based on the NIT code validation process.
 */
export function validateNITCode(nitCode) {
  return async (dispatch, getState) => {
    const {
      whitelabelConfig: {
        env: {
          api: { searchUrl },
        },
      },
      costapass,
    } = getState();
    const {
      profile: { token: costaPassToken },
    } = costapass.toJS();

    try {
      const response = await fetch(`${searchUrl}/v2/ewallet/nits/${nitCode}/validate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${costaPassToken}`,
        },
      });

      const data = await response.json();

      // Check for any type of error (HTTP or application-level)
      if (response.status === 401) {
        const errorMessage = data.message || 'Unauthorized access to validate NIT code';
        throw new Error(errorMessage);
      }

      if (response.status === 422) {
        const errorMessage = data.message || 'Invalid NIT code';
        throw new Error(errorMessage);
      }

      // Check for unauthorized access
      if (!response.ok) {
        throw new Error('Unexpected error from server');
      }

      dispatch({ type: SET_NIT_CODE_VALIDATED, payload: data });

      return data;
    } catch (error) {
      // If it's already our custom error, re-throw it
      if (error.message === 'Failed to fetch' || error.message.includes('NetworkError')) {
        throw new Error('Connection error. Verify your internet connection and try again.');
      }
      // For network errors, throw generic message
      throw error;
    }
  };
}

/**
 * Confirms the OTP entered by the user.
 *
 * @param {string} otp - The one-time password entered by the user.
 * @returns {Function} A thunk action that dispatches actions based on the OTP confirmation process.
 */
export function confirmOTP(otp) {
  return (dispatch) => {
    const {
      whitelabelConfig: { features },
    } = store.getState();
    dispatch(loadingOTP({ loading: true, showOTPModal: true }));
    purchase
      .validateOTP(getOTPEndpoint(), otp)
      .then(() => {
        dispatch(otpConfirmation({ otpConfirmed: true, showOTPModal: false }));
      })
      .catch((error) => {
        const { code } = error;
        const isSsoUnauthorized = code === 401 && features.USE_SSO;
        dispatch(
          otpConfirmation({
            otpConfirmed: false,
            showOTPModal: !isSsoUnauthorized,
          }),
        );
        if (isSsoUnauthorized) {
          dispatch(ssoError(code));
        } else {
          dispatch(otpError({ otpError: 'otp_confirmation_error', otpErroCode: code }));
        }
      });
  };
}

/**
 * Resolve a Cybersource decline reason from either failure shape.
 *
 * A rejected `purchase.createPayment` carries `{ engine, reason }`; a failure discovered by
 * polling carries `{ paymentEngine, errorReason }` — the shape the Adyen, Stripe and
 * MercadoPago branches already read. The decline is the same event either way, so it maps
 * to the same localized message (openspec/specs/cybersource-decline-messages).
 *
 * @param {Object} error - The failure object handed to paymentFailureHandler.
 * @returns {String|undefined} the decline reason, or undefined when this is not a
 *   Cybersource decline.
 */
function getCybersourceDeclineReason(error) {
  if (!error) return undefined;

  const engine = error.engine || error.paymentEngine;
  if (engine !== availablePaymentEngines.cybersource) return undefined;

  return error.reason || error.errorReason || undefined;
}

/**
 * Checks if the error indicates a Cybersource DM rejection eligible for 3DS retry.
 * @param {Object} error - The error object from the payment attempt.
 * @returns {boolean} True if the error is a Cybersource DM rejection.
 */
function isCybersourceRejection(error) {
  if (!error) return false;

  const dmAction = error.cybersourceDmAction || error.cybersource_dm_action;
  return Boolean(dmAction && ['hard_reject', 'review'].includes(dmAction));
}

/**
 * Whether Purchase refused the payment with the given readiness code. The code comes either alone or
 * in a list (`details.code`).
 * @param {object} error - Payment failure.
 * @param {string} code - Purchase error code.
 * @returns {boolean} True when the failure carries the code.
 */
export function hasPaymentErrorCode(error, code) {
  const codes = error?.details?.code;
  return Array.isArray(codes) ? codes.includes(code) : codes === code;
}

/**
 * MER-701: Purchase refused the payment because the total no longer reaches the method's minimum.
 * The reloaded purchase leaves that method out, so it just disappears from the checkout, with no
 * message. If the reload fails or still offers the method, the buyer gets the generic error instead
 * of a silent dead end on every click.
 * @param {Function} dispatch - The Redux dispatch function.
 */
function recoverFromInvalidAmount(dispatch) {
  const purchaseState = store.getState().purchase;
  const token = purchaseState.get('token');
  const rejected = purchaseState.get('selectedPaymentMethod') || {};
  const rejectedOption = purchaseState.get('selectedPaymentOption');

  Promise.resolve(
    dispatch(
      getPurchase(token, true, {
        preservePaymentOptionSelection: true,
        quiet: true,
        // The checkout may have moved to another purchase while this reload was pending.
        isCurrent: () => store.getState().purchase.get('token') === token,
      }),
    ),
  ).then((result) => {
    if (result?.superseded) return;
    // getPurchase keeps the selection only while the method is still offered (Yuno and legacy
    // options included), and a failed quiet reload leaves it untouched. A legacy option has an
    // empty method, so only the option tells whether it was withdrawn.
    const { purchase } = store.getState();
    const current = purchase.get('selectedPaymentMethod') || {};
    const stillOffered =
      purchase.get('selectedPaymentOption') === rejectedOption &&
      current.type === rejected.type &&
      current.engine === rejected.engine &&
      current.provider === rejected.provider;
    if (stillOffered) {
      dispatch(setError(301, 'error_generating_payment', 'warning', false));
      dispatch(setCardErrorOccured());
    }
  });
}

/**
 * Handles payment failures by dispatching appropriate error actions and logging the error.
 *
 * @param {Function} dispatch - The Redux dispatch function.
 * @param {Object} error - The error object containing details about the payment failure.
 * @param {boolean} [storePayment=false] - Optional flag to indicate if the payment was a store payment.
 * @param {Object} [paymentContext={}] - Context from the submitted payment flow.
 */
export function paymentFailureHandler(dispatch, error, storePayment = false, paymentContext = {}) {
  const state = store.getState();
  const { features } = state.whitelabelConfig;
  const { enableThreeDSOnRetry } = state.whitelabelConfig.env?.cielo || {};
  const threeDSRetryAttempted = state.payment.get('threeDSRetryAttempted');

  // RM-1201: intercept the typed BFO refresh-required rejection at the raw failure
  // boundary, before provider-specific translation discards the nested details. In a
  // supported journey it enters BFO seat-reselection recovery and suppresses the
  // generic payment error; excluded flows keep their existing outcomes.
  if (isBfoRefreshRequiredPaymentError(error)) {
    const plainPurchase = state.purchase.toJS();
    if (
      isBfoSupportedJourney({
        purchase: plainPurchase,
        features,
        brand: state.whitelabelConfig.env?.brand,
      }) &&
      isBfoReturnedPurchase(plainPurchase)
    ) {
      dispatch(updatePurchase(false));
      dispatch(receivePayment());
      dispatch(recoverFromBfoPriceRefresh());
      return;
    }
  }

  // If enableThreeDSOnRetry is active, the error comes from Cybersource,
  // and we haven't already attempted a 3DS retry, trigger retry. Not on a Basis Theory
  // attempt: the retry re-submits the legacy card form, which is not mounted then, and the
  // Token Intent has already been consumed.
  if (
    enableThreeDSOnRetry &&
    !threeDSRetryAttempted &&
    paymentContext.cardCheckoutMode !== 'basis_theory' &&
    isCybersourceRejection(error)
  ) {
    dispatch({ type: TRIGGER_3DS_RETRY, amount: error.amount });
    return;
  }

  dispatch(updatePurchase(false));
  dispatch(receivePayment());

  // Named apart from the analytics `cybersourceDeclineReason` below, which follows the
  // rendered branch's precedence rather than deciding which branch renders.
  const cybersourceMessageReason = getCybersourceDeclineReason(error);

  if (hasPaymentErrorCode(error, 'invalid_amount')) {
    // First: a 417 may also carry a human reason or come from a store payment, and either
    // branch below would show a message MER-701 asks to leave out.
    recoverFromInvalidAmount(dispatch);
  } else if (isPaymentPreconditionError(error)) {
    // Refused before any request went out — a required session that expired during 3D Secure, say.
    // The card is fine; telling the buyer it failed sends them to change a card that was never the
    // problem. The refusal names its own message and follow-up, so this stays brand-neutral.
    dispatch(setError(301, error.notificationKey, 'warning', false));
    if (error.followUp) dispatch(error.followUp());
  } else if (error.humanErrorReason) {
    dispatch(setError(301, 'null', 'warning', false, error.humanErrorReason));
    dispatch(setCardErrorOccured());
  } else if (cybersourceMessageReason) {
    dispatch(setError(301, getCybersourceErrorKey(cybersourceMessageReason), 'warning', false));
    dispatch(setCardErrorOccured());
  } else if (error.paymentEngine === 'adyen' && error.errorReason) {
    const adyenKey = `adyen.${error.errorReason}`;
    const hasTranslation = i18n.exists(`warning.${adyenKey}`, { ns: 'notifications' });
    dispatch(setError(301, hasTranslation ? adyenKey : 'adyen.default_error', 'warning', false));
    dispatch(setCardErrorOccured());
  } else if (
    error.paymentEngine === 'mercadopago' &&
    error.errorReason === 'invalid_card_number_luhn_validation'
  ) {
    // MercadoPago tokenizes cards that fail Luhn validation; the backend blocks the
    // charge and returns this reason so we can ask the user to fix the card number.
    dispatch(setError(301, 'check_your_card_details', 'warning', false));
    dispatch(setCardErrorOccured());
  } else if (error.paymentEngine === 'stripe' && error.errorReason) {
    const reasonKey = error.errorReason === 'default' ? 'default_error' : error.errorReason;
    // Wallet-specific messages (e.g. stripe.google_pay.incorrect_cvc,
    // stripe.apple_pay.incorrect_cvc) take precedence when the payment type provides them;
    // otherwise the shared stripe.* card keys apply to wallets too. Without a paymentType (TDC)
    // only the shared keys are looked up.
    const candidateKeys = error.paymentType
      ? [`stripe.${error.paymentType}.${reasonKey}`, `stripe.${reasonKey}`]
      : [`stripe.${reasonKey}`];
    const stripeKey = candidateKeys.find((key) => {
      return i18n.exists(`warning.${key}`, { ns: 'notifications' });
    });
    dispatch(setError(301, stripeKey || 'stripe.default_error', 'warning', false));
    dispatch(setCardErrorOccured());
  } else if (error.code === 403) {
    dispatch(setError(301, 'finished_time_for_purchase', 'warning', false));
    dispatch(expirePurchase());
  } else if (error.code === 401 && features.USE_SSO) {
    dispatch(ssoError(error.code));
  } else if (storePayment) {
    // AM-1686: a store reference may have just been issued backend-side. Refresh only that
    // flag (lightweight) so QuickCheckout blocks seat/passenger edits up front, and pick the
    // right message: with a reference, editing is blocked, so "choose other seats" would be
    // misleading — tell the user the reservation couldn't be confirmed instead.
    const purchaseToken = store.getState().purchase.get('token');
    const showStoreFailureError = (referenceIssued) =>
      dispatch(
        setError(
          301,
          referenceIssued ? 'store_reference_payment_error' : 'choose_other_seats',
          'warning',
          false,
        ),
      );
    if (purchaseToken) {
      Promise.resolve(dispatch(refreshStoreReferenceIssued(purchaseToken)))
        .then(showStoreFailureError)
        .catch(() => showStoreFailureError(false));
    } else {
      showStoreFailureError(false);
    }
  } else if ((error.data && error.data.category === 'request') || error.message_to_purchaser) {
    dispatch(setError(300, 'check_your_card_details', 'warning', false));
  } else if (error?.details?.code === 'unavailable') {
    const searchParams = getSearchParams();
    const redirectURL = generateSearchRedirectUrl(searchParams);
    dispatch(setError(301, 'schedule_unavailable', 'warning', true, undefined, redirectURL));
    // When the payment fails in multiple scenarios (like Doters response), the error message is in the human_error_reason field
    // No use of camelCase here because the error object comes directly from the API and is not set to state
  } else if (error?.details?.human_error_reason) {
    dispatch(setError(301, 'null', 'warning', false, error.details.human_error_reason));
  } else if (error.code === 51) {
    dispatch(setError(301, 'error_generating_payment_doters', 'warning', false));
  } else {
    dispatch(setError(301, 'error_generating_payment', 'warning', false));
    dispatch(setCardErrorOccured());
  }

  // Cybersource groups its declines into the five IAMSA message families (MSI-1509), so report
  // which family the user actually read. Which field named that reason depends on which branch
  // above rendered: a backend `humanErrorReason` was computed from the payment's raw reason, which
  // reaches us as `errorReason`, while the reason-lookup branch renders from `reason`. Following the
  // same precedence keeps the reported family from describing a message nobody saw, on the one path
  // where an error object carries both fields with different values.
  const provider = error?.engine || error?.paymentEngine;
  const hasBackendMessage = Boolean(error?.humanErrorReason);
  const cybersourceDeclineReason = hasBackendMessage
    ? error?.errorReason || error?.reason
    : error?.reason || error?.errorReason;
  const cybersourceDisplayCode =
    provider === 'cybersource' ? getCybersourceDisplayCode(cybersourceDeclineReason) : undefined;

  trackPurchaseStepError({
    code: error?.code || error?.reason || error?.errorReason,
    message: error?.humanErrorReason || error?.message || error?.message_to_purchaser,
    step: 'payment',
    provider,
    displayCode: cybersourceDisplayCode,
    // When the failure names its engine, that's the one that actually processed the charge; the
    // tracker falls back to the purchase state for the failures that carry no engine at all.
    paymentEngine: provider,
  });
  errorMonitoring.notify({ error, info: 'Error on paymentFailureHandler' });
  console.error('Error on paymentFailureHandler', error);
}

/**
 * Handles the polling of payment status updates and dispatches actions accordingly.
 *
 * @param {Function} dispatch - The Redux dispatch function.
 * @param {string} purchaseToken - The token associated with the purchase.
 * @param {Object} response - The response object from the payment status request.
 * @param {Object} [paymentContext={}] - Context from the submitted payment flow.
 */
export function paymentPollingHandler(dispatch, purchaseToken, response, paymentContext = {}) {
  const { status, payload } = camelizeKeys(response);
  const { purchaseState, using3dSecure, humanErrorReason } = payload;
  const isBasisTheoryChargedAttempt =
    paymentContext.cardCheckoutMode === 'basis_theory' &&
    payload.status === 'charged' &&
    purchaseState === 'attempt';

  if (
    isBasisTheoryChargedAttempt ||
    (payload.status === 'pending' && using3dSecure) ||
    purchaseState !== 'attempt'
  ) {
    dispatch(receivePayment(payload));
    dispatch(getPurchase(purchaseToken));
  } else if (
    !['pending', 'completed'].includes(status) ||
    ['failed', 'rejected'].includes(payload.status)
  ) {
    // Pix and Nequi Case
    if (
      purchaseState === 'attempt' &&
      (payload.paymentType === 'transfer' || payload.paymentType === 'app_notify') &&
      payload.status === 'pending'
    ) {
      dispatch(receivePayment(payload));
      dispatch(getPurchase(purchaseToken));
    } else if (status === 'retries_exceeded') {
      dispatch(setError(301, 'error_generating_payment', 'warning'));
      console.error('Payment retries exceeded');
    } else if (status === 'aborted' && payload.status !== 'failed') {
      dispatch(receivePayment(payload));
      dispatch(getPurchase(purchaseToken));
    } else {
      // Check if this is a Cybersource DM rejection eligible for 3DS retry
      const currentState = store.getState();
      const { enableThreeDSOnRetry } = currentState.whitelabelConfig.env?.cielo || {};
      const threeDSRetryAlreadyAttempted = currentState.payment.get('threeDSRetryAttempted');
      if (
        enableThreeDSOnRetry &&
        !threeDSRetryAlreadyAttempted &&
        paymentContext.cardCheckoutMode !== 'basis_theory' &&
        ['hard_reject', 'review'].includes(payload.cybersourceDmAction)
      ) {
        dispatch({ type: TRIGGER_3DS_RETRY, amount: payload.amount });
        return;
      }

      // ELSE, is an error of some kind
      // Create detailed error with payload information for Sentry
      const errorDetails = {
        message: 'PaymentPollingError',

        // Payment identification
        paymentId: payload?.id,
        referenceId: payload?.referenceId,
        paymentEngine: payload?.paymentEngine,
        paymentType: payload?.paymentType,

        // Payment state
        status,
        payloadStatus: payload?.status,
        purchaseState,

        errorMessage: payload?.errorMessage,

        // Card/transaction information
        cardBrand: payload?.cardBrand,
        amount: payload?.amount,
        deviceSessionId: payload?.deviceSessionId,
        trackerId: payload?.trackerId,

        // Timestamps
        createdAt: payload?.createdAt,
        paidAt: payload?.paidAt,

        // Bank information
        bankId: payload?.bankId,
        bankName: payload?.bankName,
        bankDetails: payload?.bankDetails,
      };

      const PaymentPollingError = new Error('Payment Polling Error');

      // Send to Sentry only if there's no humanErrorReason (generic error case)
      if (!humanErrorReason) {
        errorMonitoring.notify({
          error: PaymentPollingError,
          info: errorDetails,
        });
      }

      const failureInfo = {
        paymentEngine: payload?.paymentEngine,
        paymentType: payload?.paymentType,
        errorReason: payload?.errorReason,
      };
      if (payload?.paymentEngine === 'stripe' && !failureInfo.errorReason) {
        failureInfo.errorReason = 'default';
      }
      // MercadoPago Luhn failures must keep their dedicated error path, so don't let a
      // generic humanErrorReason mask the invalid_card_number_luhn_validation handling.
      const isMercadoPagoLuhnError =
        payload?.paymentEngine === 'mercadopago' &&
        payload?.errorReason === 'invalid_card_number_luhn_validation';
      if (humanErrorReason && payload?.paymentEngine !== 'stripe' && !isMercadoPagoLuhnError) {
        failureInfo.humanErrorReason = humanErrorReason;
      }

      paymentFailureHandler(dispatch, failureInfo, false, paymentContext);
    }
  }
}

/**
 * Initiates a payment attempt using Efecty.
 *
 * @returns {Function} A thunk action that dispatches actions based on the Efecty payment process.
 */
export function efectyPaymentAttempt() {
  return (dispatch, getState) => {
    const { purchase: purchaseState } = getState();
    const purchaseToken = purchaseState.get('token');
    const paymentPayload = {
      payment_type: 'store',
      payment_engine: availablePaymentEngines.mercadoPago,
      payment_provider: 'efecty',
      tracker_id: getRnMixpanelBridgedTrackerId(),
      device_session_id: getSessionId(),
      monthly_selected_plan: 1,
    };
    dispatch(requestPayment());
    return createPayment(purchaseToken, paymentPayload, {
      onReceivePayment: (response) => {
        paymentPollingHandler(dispatch, purchaseToken, response);
      },
    }).catch((error) => paymentFailureHandler(dispatch, error));
  };
}

/**
 * Initiates a payment attempt using BNPL.
 *
 * @returns {Function} A thunk action that dispatches actions based on the BNPL payment process.
 */
export function bnplPaymentAttempt() {
  return (dispatch, getState) => {
    const { purchase: purchaseState } = getState();
    const purchaseToken = purchaseState.get('token');
    const selectedPaymentMethod = purchaseState.get('selectedPaymentMethod');
    const paymentEngine = selectedPaymentMethod?.engine ?? 'kueski_pay';
    const paymentProvider = selectedPaymentMethod?.provider ?? 'kueski';
    const extra = selectedPaymentMethod?.extra ?? {};

    const bnplPayment = bnplFactory.create({ engine: paymentEngine });

    const bnplInfo = {
      payment_type: 'bnpl',
      payment_engine: paymentEngine,
      payment_provider: paymentProvider,
      tracker_id: getRnMixpanelBridgedTrackerId(),
      device_session_id: getSessionId(),
      monthly_selected_plan: 1,
    };

    dispatch(requestPayment());

    return bnplPayment
      .createPayload(bnplInfo, { ...extra })
      .then((paymentPayload) => {
        return createPayment(purchaseToken, paymentPayload, {
          onReceivePayment: (response) => {
            window.location = response.payload.redirect_to;
          },
        }).catch((error) => paymentFailureHandler(dispatch, error));
      })
      .catch((error) => paymentFailureHandler(dispatch, error));
  };
}

/**
 * Initiates a payment attempt using Wallet.
 *
 * @returns {Function} A thunk action that dispatches actions based on the Wallet payment process.
 */
export function walletPaymentAttempt() {
  return (dispatch, getState) => {
    const { purchase: purchaseState } = getState();
    const purchaseToken = purchaseState.get('token');
    const selectedPaymentMethod = purchaseState.get('selectedPaymentMethod');
    const paymentEngine = selectedPaymentMethod?.engine ?? 'mercadopago';
    const paymentProvider = selectedPaymentMethod?.provider ?? 'mercadopago';
    const extra = selectedPaymentMethod?.extra ?? {};

    dispatch(requestPayment());
    dispatch(updatePurchase(true));

    const walletPayment = walletFactory.create({ engine: paymentEngine });
    const walletPayload = {
      payment_type: 'wallet',
      payment_engine: paymentEngine,
      payment_provider: paymentProvider,
      tracker_id: getRnMixpanelBridgedTrackerId(),
      device_session_id: getSessionId(),
      monthly_selected_plan: 1,
    };

    return walletPayment
      .createPayload(walletPayload, { ...extra })
      .then((paymentPayload) => {
        return createPayment(purchaseToken, paymentPayload, {
          onReceivePayment: (response) => {
            window.location = response.payload.redirect_to;
          },
        }).catch((error) => paymentFailureHandler(dispatch, error));
      })
      .catch((error) => paymentFailureHandler(dispatch, error));
  };
}

/**
 * Initiates a payment attempt based on the payment type.
 *
 * @param {string} token - The token representing the payment session.
 * @param {string} type - The type of payment to attempt (e.g., 'paypal').
 * @returns {Function} A thunk action that dispatches actions based on the payment attempt process.
 */
export function paymentAttempt(token, type) {
  return (dispatch) => {
    dispatch(updatePurchase(true));
    dispatch(requestPayment());

    if (type === 'paypal') {
      return createPayment(token, getPaymentPayload(type, type), { init: false })
        .then(({ payload }) => dispatch(receivePayment(payload)))
        .catch((error) => paymentFailureHandler(dispatch, error));
    }

    return createPayment(token, getPaymentPayload(type), {
      onReceivePayment: (response) => paymentPollingHandler(dispatch, token, response),
    }).catch((error) => paymentFailureHandler(dispatch, error));
  };
}

/**
 * Initiates a credit card payment attempt.
 *
 * @param {string} purchaseToken - The token associated with the purchase.
 * @param {Object} cardValues - The card values and billing address information.
 * @param {Object} paymentInfo - Additional payment information.
 * @returns {Function} A thunk action that dispatches actions based on the credit card payment process.
 */
export function cardPaymentAttempt(purchaseToken, cardValues, paymentInfo = {}) {
  return (dispatch, getState) => {
    const state = getState();
    const { purchase: purchaseState, whitelabelConfig } = state;
    dispatch(updatePurchase(true));
    dispatch(requestPayment());

    // this is the new way to get the payment engine for each payment method
    const selectedPaymentMethod = purchaseState.get('selectedPaymentMethod');
    const extra = selectedPaymentMethod?.extra ?? {};
    // defaults to purchase payment engine for backwards compatibility
    const paymentEngine = selectedPaymentMethod?.engine ?? purchaseState.get('paymentEngine');
    const useBasisTheoryStrategy = shouldUseBasisTheoryStrategy(
      state,
      selectedPaymentMethod,
      cardValues,
      paymentInfo.cardCheckoutMode,
    );
    const strategyEngine = useBasisTheoryStrategy ? 'basis_theory' : paymentEngine;
    const paymentContext = {
      cardCheckoutMode: useBasisTheoryStrategy ? 'basis_theory' : 'legacy',
    };
    const cardPayment = cardFactory.create({ engine: strategyEngine });
    const { enableThreeDS = false, enableSilentOrderPost = false } =
      whitelabelConfig.env?.cielo || {};

    const USE_NEW_CARD_INPUT = whitelabelConfig.features?.USE_NEW_CARD_INPUT ?? false;
    const USE_NEW_CARD_STRUCTURE = whitelabelConfig.features?.USE_NEW_CARD_STRUCTURE ?? false;
    const UNIFIED_USE_NEW_CARD = USE_NEW_CARD_INPUT && USE_NEW_CARD_STRUCTURE;

    let cardValuesSanitized = { ...cardValues };
    if (UNIFIED_USE_NEW_CARD && cardValues.expirationDate) {
      const sanitizedCardNumber = sanitizeCardNumber(cardValues.cardNumber);
      const [expirationMonth, expirationYear] = cardValues.expirationDate.split('/');

      const fullYear = `20${expirationYear}`;

      cardValuesSanitized = {
        ...cardValues,
        expirationMonth,
        expirationYear: fullYear,
        cardNumber: sanitizedCardNumber,
      };
    }

    return cardPayment
      .createPayload(cardValuesSanitized, {
        ...extra,
        ...paymentInfo,
        enableThreeDS: paymentInfo.skipThreeDS ? false : enableThreeDS,
        enableSilentOrderPost,
        isThreeDSRetry: paymentInfo.isThreeDSRetry || false,
      })
      .then((enginePayload) => {
        const paymentPayload = {
          ...getPaymentPayload('credit_card', paymentEngine, cardValuesSanitized),
          ...enginePayload,
        };
        return createPayment(purchaseToken, paymentPayload, {
          onReceivePayment: (response) => {
            paymentPollingHandler(dispatch, purchaseToken, response, paymentContext);
          },
        }).catch((error) => {
          paymentFailureHandler(dispatch, error, false, paymentContext);
        });
      })
      .catch((error) => paymentFailureHandler(dispatch, error, false, paymentContext));
  };
}

/**
 * Initiates a credit card payment attempt using a pre-generated Cybersource payload.
 * This action skips the payload creation step and uses the provided payload directly.
 *
 * @param {Object} paymentData - The payment data object.
 * @param {string} paymentData.purchaseToken - The token associated with the purchase.
 * @param {Object} paymentData.cardValues - Basic card information and billing address.
 * @param {Object} paymentData.cybersourcePayload - The pre-generated Cybersource payload.
 * @param {Object} [paymentData.paymentInfo={}] - Additional payment information.
 * @returns {Function} A thunk action that dispatches actions based on the credit card payment process.
 */
export function cybersourcePaymentAttempt(paymentData) {
  const { purchaseToken, cardValues, cybersourcePayload, paymentInfo = {} } = paymentData;
  return (dispatch) => {
    dispatch(updatePurchase(true));
    dispatch(requestPayment());

    try {
      // Create the payment payload combining the card values, Cybersource payload, and additional payment info
      const paymentPayload = {
        ...getPaymentPayload('credit_card', 'cybersource', cardValues),
        ...cybersourcePayload,
        ...paymentInfo,
      };

      // Process the payment
      return createPayment(purchaseToken, paymentPayload, {
        onReceivePayment: (response) => {
          paymentPollingHandler(dispatch, purchaseToken, response);
        },
      }).catch((error) => {
        paymentFailureHandler(dispatch, error);
      });
    } catch (error) {
      return paymentFailureHandler(dispatch, error);
    }
  };
}

/**
 * Initiates a store payment attempt.
 *
 * @param {Object} paymentData - The payment data required for store payment.
 * @param {string} paymentData.token - The token representing the payment session.
 * @param {Object} paymentData.fields - Additional fields required for the payment.
 * @returns {Function} A thunk action that dispatches actions based on the store payment process.
 */
export function storePaymentAttempt({ token, ...fields }) {
  return (dispatch, getState) => {
    // this is the new way to get the payment engine for each payment method
    const selectedPaymentMethod = getState().purchase.get('selectedPaymentMethod');
    // defaults to kushki for backwards compatibility
    const paymentEngine = selectedPaymentMethod?.engine ?? 'kushki';

    // Resolved before any loading flag: an engine without a store strategy used to throw here
    // after `updatePurchase(true)` and `requestPayment()`, leaving the pay button spinning forever
    // (PRO2-147). Nothing was sent, so this is not a store failure for `paymentFailureHandler`,
    // whose store branch would refresh the store reference and say "choose other seats".
    let storePayment;
    try {
      storePayment = storeFactory.create({ engine: paymentEngine });
    } catch (error) {
      errorMonitoring.notify({ error, info: { from: 'storePaymentAttempt', paymentEngine } });
      dispatch(setError(301, 'error_generating_payment', 'warning', false));
      return Promise.resolve();
    }

    dispatch(updatePurchase(true));
    dispatch(requestPayment());

    return storePayment
      .getPayload(fields)
      .then((enginePayload) => {
        const payload = {
          ...getPaymentPayload('store', paymentEngine),
          ...enginePayload,
        };

        return createPayment(token, payload, {
          onReceivePayment: (response) => paymentPollingHandler(dispatch, token, response),
        }).catch((error) => paymentFailureHandler(dispatch, error, true));
      })
      .catch((error) => paymentFailureHandler(dispatch, error, true));
  };
}

/**
 * Initiates a third-party payment attempt.
 *
 * @param {string} token - The token representing the payment session.
 * @param {Object} paymentData - The payment data required for the third-party payment.
 * @param {string} paymentData.engine - The payment engine to be used.
 * @param {string} paymentData.type - The type of payment to attempt.
 * @returns {Function} A thunk action that dispatches actions based on the third-party payment attempt process.
 */
export function thirdPartyPaymentAttempt(token, { engine, type }) {
  return (dispatch) => {
    dispatch(updatePurchase(true));
    dispatch(requestPayment());

    return createPayment(token, getPaymentPayload(type, engine), { init: false })
      .then(({ payload }) => dispatch(receivePayment(payload)))
      .catch((error) => paymentFailureHandler(dispatch, error));
  };
}

/**
 * Retrieves the payment details for a given payment ID and purchase token.
 *
 * @param {string} paymentId - The ID of the payment to retrieve.
 * @param {string} purchaseToken - The token associated with the purchase.
 * @returns {Function} A thunk action that dispatches actions to handle the payment retrieval process.
 */
export function getPayment(paymentId, purchaseToken) {
  return (dispatch) => {
    dispatch(requestPayment());
    // return a promise to wait for
    return Promise.all([purchase.get(purchaseToken), purchase.getPayment(purchaseToken, paymentId)])
      .then(([purchaseResponse, paymentResponse]) => {
        dispatch(receivePurchase(purchaseResponse));
        dispatch(receivePayment(paymentResponse));
      })
      .catch((error) => {
        dispatch(setError(304, 'error_when_looking_for_payment', 'error', true));
        dispatch(receivePayment());
        throw new Error(`Error 304: Failed to get payment. ${error.message}`);
      });
  };
}

/**
 * Polls the payment service for a transfer payment result within a specified time frame.
 *
 * @param {Object} config - The configuration object for the payment polling.
 * @param {string} config.purchaseToken - The token associated with the purchase.
 * @param {string} config.paymentId - The ID of the payment to poll for.
 * @param {number} [config.minutes=21] - The total duration in minutes to poll for the payment result.
 * @param {number} [config.interval=2] - The interval in seconds between each poll attempt.
 * @returns {Promise<Object>} A promise that resolves with the payment result or rejects with an error.
 */
export function transferPaymentResult({ purchaseToken, paymentId, minutes = 21, interval = 2 }) {
  return new Promise((resolve, reject) => {
    const pollOptions = {
      interval: interval * 1000,
      maxRetries: (minutes * 60) / interval,
    };

    /**
     * Callback function for handling the payment response.
     *
     * @callback onReceivePayment
     * @param {Object} poll - The response object from the payment request.
     */
    const onReceivePayment = (poll) => {
      if (!['pending', 'completed'].includes(poll.status)) {
        return reject(camelizeKeys(poll.payload));
      }

      const status = poll.payload.kushki_processor_state;
      const purchaseState = poll.payload.purchase_state;
      const paymentEngine = poll.payload.payment_engine;
      const paymentStatus = poll.payload.status;

      if (paymentEngine === 'mercadopago' && paymentStatus === 'charged') {
        resolve(camelizeKeys(poll.payload));
      }

      switch (status) {
        case 'OK':
          if (purchaseState !== 'attempt') {
            resolve(camelizeKeys(poll.payload));
          }
          break;
        case 'PENDING':
          break;
        default:
          /**
           * When the user cancels the payment (using Mercado Pago) by going back from the PSE page to the checkout
           * the Purchase API doesn't set the kushki_processor_state property,
           * this causes the poll to fail and the promise to be rejected.
           *
           * This is a workaround to avoid the rejection of the promise until the poll.status is "aborted"
           */
          if (paymentEngine === availablePaymentEngines.mercadoPago && poll.status === 'pending') {
            break;
          }
          reject(camelizeKeys(poll.payload));
          break;
      }
    };

    purchase.getPayment(purchaseToken, paymentId, {
      onReceivePayment,
      options: pollOptions,
    });
  });
}

/**
 * Initiates the polling of transfer payment status and dispatches actions accordingly.
 *
 * @param {Object} paymentConfig - The configuration object for the transfer payment polling.
 * @returns {Function} A thunk action that dispatches actions based on the transfer payment polling process.
 */
export function pollTransferPayment(paymentConfig) {
  return (dispatch) => {
    transferPaymentResult(paymentConfig)
      .then((response) => {
        dispatch(receivePayment(response));
      })
      .catch((response) => {
        const error = response.kushkiProcessorState;

        if (error === 'NOT_AUTHORIZED') {
          dispatch(setError(300, 'transaction_declined', 'warning', false));
        } else if (error === 'FAILED') {
          dispatch(setError(301, 'transaction_failed', 'warning', false));
        } else {
          dispatch(setError(301, 'error_generating_payment', 'warning', false));
        }

        /**
         * For some reason, the payment data can be returned either in the response.payload or directly in the response.
         * This was causing a bug that prevented the user from being redirected back to the checkout when the payment failed.
         */
        dispatch(receivePayment(response.payload ? response.payload : response));
        dispatch(updatePurchase(false));
      });
  };
}

/**
 * Initiates a transfer payment attempt.
 *
 * @param {Object} config - The configuration object for the transfer payment.
 * @param {string} config.engine - The payment engine to be used.
 * @param {string} config.provider - The payment provider.
 * @param {string} config.purchaseToken - The token associated with the purchase.
 * @param {string} config.bankId - The ID of the bank for the transfer.
 * @returns {Function} A thunk action that dispatches actions based on the transfer payment attempt process.
 */
export function transferPaymentAttempt(config) {
  return async (dispatch, getState) => {
    try {
      const { engine, provider, purchaseToken, bankId, addressAttributes } = config;
      const transferFactoryPayload = engine
        ? { engine, provider }
        : { engine: 'kushki', provider: 'pse' };
      const transfer = transferFactory.create(transferFactoryPayload);

      dispatch(updatePurchase(true));
      dispatch(requestPayment());

      const envConfig = getState().whitelabelConfig.env;
      const token = await transfer.createToken({
        ...config,
        currency: envConfig.currency,
        callbackUrl: `${envConfig.api.purchaseUrl}/v2/kushki/transfer_in_redirect`,
      });

      /**
       * @todo Add a method called getEntityTypes(personType) to the Transfer template
       * and remove this conditional when the method is implemented
       */
      const mercadoPagoEntityTypes = ['individual', 'association'];
      const isMercadoPago = engine === availablePaymentEngines.mercadoPago;
      const entityType = isMercadoPago ? mercadoPagoEntityTypes[config.personType] : '';
      const paymentTypesByEngines = {
        nequi: 'app_notify',
        default: 'transfer',
      };

      const createTransferPayload = {
        payment_type: paymentTypesByEngines[engine] || paymentTypesByEngines.default,
        payment_engine: engine || 'kushki',
        payment_provider: provider,
        tracker_id: getRnMixpanelBridgedTrackerId(),
        device_session_id: getSessionId(),
        monthly_selected_plan: 1,
        transfer_token: token,
        bank_id: bankId,
        entity_type: entityType,
        address_attributes: decamelizeKeys(addressAttributes),
      };

      if (engine === 'nequi') {
        return createPayment(purchaseToken, createTransferPayload, {
          onReceivePayment: (response) => {
            /**
             * Nequi has payment_type = 'app_notify', so we need to change it to 'transfer'
             */
            response.payload.payment_type = 'transfer';
            if (response.payload.human_error_reason) {
              paymentFailureHandler(dispatch, {
                humanErrorReason: response.payload.human_error_reason,
              });
            } else {
              paymentPollingHandler(dispatch, purchaseToken, response);
            }
          },
        }).catch((error) => paymentFailureHandler(dispatch, error));
      }

      const response = await createPayment(purchaseToken, createTransferPayload, {
        init: false,
      });

      if (transfer.needsRedirect()) {
        window.location = response.payload.redirect_to;
      } else {
        paymentPollingHandler(dispatch, purchaseToken, response);
      }
    } catch (error) {
      paymentFailureHandler(dispatch, error);
    }
  };
}

/**
 * Initiates a payment attempt using Evertec.
 *
 * @param {Object} config - The configuration object for the Evertec payment attempt.
 * @param {string} config.purchaseToken - The token associated with the purchase.
 * @param {string} config.type - The type of payment to attempt.
 * @param {string} config.provider - The payment provider.
 * @param {string} config.requestId - The request ID for the payment.
 * @returns {Function} A thunk action that dispatches actions based on the Evertec payment process.
 */
export function evertecPaymentAttempt({ purchaseToken, type, provider, requestId }) {
  return (dispatch) => {
    try {
      const paymentPayload = {
        payment_engine: 'evertec',
        payment_type: type,
        payment_provider: provider,
        tracker_id: getRnMixpanelBridgedTrackerId(),
        device_session_id: getSessionId(),
        reference_id: requestId,
      };
      return createPayment(purchaseToken, paymentPayload, { init: false })
        .then(({ payload }) => dispatch(receivePayment(payload)))
        .catch((error) => {
          paymentFailureHandler(dispatch, error);
          throw new Error(`Error on evertecPaymentAttempt: ${error}`);
        });
    } catch (error) {
      paymentFailureHandler(dispatch, error);
      throw new Error(`Error on paymentPayload for evertecPaymentAttempt: ${error}`);
    }
  };
}

/**
 * Confirms an Evertec payment by fetching the payment status and updating the purchase.
 *
 * @returns {Function} A thunk action that dispatches actions to handle the Evertec payment confirmation process.
 */
export function confirmEvertecPayment() {
  return (dispatch, getState) => {
    dispatch(requestPayment());
    try {
      const { purchase: purchaseState, payment: paymentState } = getState();
      const token = purchaseState.get('token');
      const paymentId = paymentState.get('id');
      return purchase.getPayment(token, paymentId).then((response) => {
        dispatch(receivePayment(response));
        dispatch(getPurchase(token));
      });
    } catch (error) {
      paymentFailureHandler(dispatch, error);
    }
  };
}

/**
 * Initiates a payment attempt using MercadoPago.
 *
 * @param {Object} mercadoPagoFormData - The form data required for MercadoPago payment.
 * @param {string} mercadoPagoFormData.token - The token representing the card data.
 * @param {number} mercadoPagoFormData.installments - The number of installments for the payment.
 * @param {string} mercadoPagoFormData.mpDeviceSessionId - The device session ID for fraud prevention.
 * @param {string} mercadoPagoFormData.paymentMethodId - The ID of the chosen payment method.
 * @param {string} mercadoPagoFormData.issuerId - The ID of the card issuer.
 * @returns {Function} A thunk action that dispatches actions based on the MercadoPago payment process.
 */
export function mercadoPagoPaymentAttempt(mercadoPagoFormData) {
  const { token, installments, mpDeviceSessionId, paymentMethodId, issuerId } = mercadoPagoFormData;
  return (dispatch, getState) => {
    const { purchase: purchaseState } = getState();
    const purchaseToken = purchaseState.get('token');

    const cardValues = {
      cardBrand: paymentMethodId,
      monthlySelectedPlan: Number(installments),
    };

    const paymentInfo = {
      mpDeviceSessionId,
      mercadoPagoToken: token,
      issuerId,
    };

    return dispatch(cardPaymentAttempt(purchaseToken, cardValues, paymentInfo));
  };
}

/**
 * Initiates a payment attempt using Yape.
 *
 * @param {Object} yapeFormData - The form data required for Yape payment.
 * @param {string} yapeFormData.otp - The one-time password from Yape app.
 * @param {string} yapeFormData.phoneNumber - The phone number associated with Yape.
 * @returns {Function} A thunk action that dispatches actions based on the Yape payment process.
 */
export function yapePaymentAttempt(yapeFormData) {
  const { otp, phoneNumber } = yapeFormData;

  return (dispatch, getState) => {
    const { purchase: purchaseState } = getState();
    const purchaseToken = purchaseState.get('token');

    dispatch(requestPayment());
    dispatch(updatePurchase(true));

    const paymentPayload = {
      payment_type: 'otp',
      payment_engine: 'mercadopago',
      phone_number: phoneNumber,
      otp,
      device_session_id: window.MP_DEVICE_SESSION_ID,
    };

    return createPayment(purchaseToken, paymentPayload, {
      onReceivePayment: (response) => {
        if (response.payload?.status === 'charged') {
          window.location = `/purchase/${purchaseToken}/complete?otp=true`;
        } else if (response.payload?.status === 'failed') {
          paymentFailureHandler(dispatch, {
            message: 'Payment failed',
            details: response.payload,
          });
        } else {
          paymentPollingHandler(dispatch, purchaseToken, response);
        }
      },
    }).catch((error) => paymentFailureHandler(dispatch, error));
  };
}

/**
 * Initiates a payment attempt using Yuno.
 *
 * @param {Object} config - The configuration object for the Yuno payment attempt.
 * @param {string} config.checkoutSession - The checkout session ID for the Yuno payment.
 * @param {string} config.merchantOrderId - The merchant order ID associated with the payment.
 * @param {Object} config.selectedPaymentMethod - The selected payment method details.
 * @param {string} config.selectedPaymentMethod.type - The type of payment (e.g., 'credit_card').
 * @param {string} config.selectedPaymentMethod.provider - The payment provider (e.g., 'yuno').
 * @param {string} config.selectedPaymentMethod.engine - The payment engine to be used.
 * @param {string} config.purchaseToken - The token associated with the purchase.
 * @returns {Function} A thunk action that dispatches actions based on the Yuno payment process.
 */
export function yunoPaymentAttempt(config) {
  const { checkoutSession, merchantOrderId, selectedPaymentMethod, purchaseToken } = config;
  const { type, provider, engine } = selectedPaymentMethod;
  return (dispatch) => {
    const paymentPayload = {
      payment_type: type,
      payment_engine: provider,
      yuno_payment_method_type: engine.toUpperCase(),
      yuno_checkout_session: checkoutSession,
      order_id: merchantOrderId,
    };

    return createPayment(purchaseToken, paymentPayload, {
      init: false,
    })
      .then(({ payload }) => {
        window.location = payload.redirect_to;
      })
      .catch((error) => {
        paymentFailureHandler(dispatch, error);
        // MER-701: the handler already reloads the purchase and owns any message. Rethrowing would
        // make quick checkout show the generic error the silent recovery leaves out.
        if (hasPaymentErrorCode(error, 'invalid_amount')) return;
        throw new Error(`Error on creating Yuno payment: ${error}`);
      });
  };
}
